import { useState } from 'react';
import { Box, CircularProgress, Alert, Typography, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { CalendarDays, ShieldPlus, BarChart3, MessageSquare } from 'lucide-react';
import { usePatientAuth } from '../../contexts/PatientAuthContext';
import {
  AuthScreenLayout, AuthEmailPassword, AuthSubmitButton, OrDivider,
  alertSx, ACCENT_BLUE, type Feature,
} from '../auth/authShared';

const FEATURES: Feature[] = [
  { icon: CalendarDays,  title: 'Upcoming Appointments', desc: 'View and manage your visits' },
  { icon: ShieldPlus,    title: 'Medical Records',       desc: 'Secure access to your health history' },
  { icon: BarChart3,     title: 'Prescriptions',         desc: 'Request refills and track medications' },
  { icon: MessageSquare, title: 'Messages',              desc: 'Connect with your care team' },
];

export function PatientLogin() {
  const { signIn } = usePatientAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await signIn(email, password);
      // PatientAuthContext takes over routing on success.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password');
      setLoading(false);
    }
  };

  return (
    <AuthScreenLayout
      heading={{ pre: 'Your health,', accent: 'simplified.' }}
      subtext="Access your appointments, records, prescriptions, and care team in one secure place."
      features={FEATURES}
      cardTitle="Patient Portal"
      cardSubtitle="Sign in to access your personal health dashboard"
    >
      <form onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}

        <AuthEmailPassword
          email={email}
          password={password}
          onEmail={setEmail}
          onPassword={setPassword}
          disabled={loading}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box component={Link} to="/forgot-password" sx={{ color: ACCENT_BLUE, fontWeight: 600, textDecoration: 'none', fontSize: '0.88rem' }}>
            Forgot password?
          </Box>
        </Box>

        <AuthSubmitButton disabled={loading}>
          {loading ? (
            <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />Signing in...</>
          ) : (
            'Sign in'
          )}
        </AuthSubmitButton>
      </form>

      <OrDivider />

      {/* Secondary entry point kept small so it doesn't compete with the primary flow. */}
      <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
        Are you staff?{' '}
        <MuiLink component={Link} to="/login" sx={{ color: '#94a3b8', textDecoration: 'underline', fontWeight: 600 }}>
          Staff sign-in
        </MuiLink>
      </Typography>
    </AuthScreenLayout>
  );
}
